import api from '@/api/client'
import { fetchAllPages } from './pagination'

/**
 * Enrollments service
 * Thin wrappers around enrollment endpoints.
 */

/**
 * Fetch enrollments with optional query parameters.
 * @param {Record<string, string|number|boolean>} params
 * @returns {Promise<any>}
 */
export const fetchEnrollments = async (params = {}) => {
  if (!params.page) return fetchAllPages('enrollments/', params)
  const res = await api.get('enrollments/', { params })
  return res.data
}

/**
 * Create a new enrollment for a DRF course id.
 * @param {number} courseId
 * @returns {Promise<any>}
 */
export const createEnrollment = async (courseId) => {
  const res = await api.post('enrollments/', { course: courseId, status: 'active' })
  return res.data
}

/**
 * Initiate payment for an existing enrollment.
 * @param {number} enrollmentId
 * @returns {Promise<any>}
 */
export const initiateEnrollmentPayment = async (enrollmentId, options = {}) => {
  const forceNew = !!options.forceNew
  const key = (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`
  const body = forceNew ? { force_new: true } : undefined
  const headers = { 'Idempotency-Key': key, ...(forceNew ? { 'X-Force-New-Transaction': 'true' } : {}) }
  const res = await api.post(`enrollments/${enrollmentId}/initiate_payment/`, body, { headers })
  return res.data
}

/**
 * Confirm a payment for an enrollment.
 * @param {number} enrollmentId
 * @param {string} transactionId
 * @param {object} providerData
 * @returns {Promise<any>}
 */
export const confirmEnrollmentPayment = async (enrollmentId, transactionId, providerData = {}) => {
  const res = await api.post(`enrollments/${enrollmentId}/confirm_payment/`, {
    transaction_id: transactionId,
    provider_data: providerData,
  })
  return res.data
}

/**
 * Reset progress for an enrollment.
 * @param {number} enrollmentId
 * @returns {Promise<any>}
 */
export const resetEnrollmentProgress = async (enrollmentId) => {
  const res = await api.post(`enrollments/${enrollmentId}/reset_progress/`)
  return res.data
}

/**
 * Track lesson access for analytics.
 * @param {number} enrollmentId
 * @param {number|string} cmsLessonId
 */
export const trackEnrollmentAccess = async (enrollmentId, cmsLessonId) => {
  await api.post(`enrollments/${enrollmentId}/track_access/`, { cms_lesson_id: cmsLessonId })
}
